import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, onSnapshot, serverTimestamp, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// Always use ki_users collection
const USERS_COLLECTION = 'ki_users';

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser]       = useState(null);
  const [userProfile, setUserProfile]       = useState(null);
  const [loading, setLoading]               = useState(true);
  const [confirmationResult, setConfirmationResult] = useState(null);

  // SeedMap AI — company + dealer role detection
  const [companyProfile, setCompanyProfile] = useState(null);
  const [userRole, setUserRole]             = useState(null); // "farmer"/"seedCompany"/"dealer"

  // On mount: check redirect result + auth state
  useEffect(() => {
    // Handle Google redirect result (fallback from popup)
    getRedirectResult(auth).catch(() => {});

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user.uid, true);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    }, (error) => {
      console.error('Auth state error:', error);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // SeedMap AI — detect role from sm_seedCompanies / sm_dealers
  // Also syncs FassalkartAI premium to seedmapPlan so SeedMap AI
  // components can read userProfile.seedmapPlan === "premium"
  useEffect(() => {
    if (!currentUser) {
      setCompanyProfile(null);
      setUserRole(null);
      return;
    }
    const unsubCompany = onSnapshot(
      doc(db, "sm_seedCompanies", currentUser.uid),
      (snap) => {
        if (snap.exists()) {
          setCompanyProfile({ id: snap.id, ...snap.data() });
          setUserRole("seedCompany");
        } else {
          getDoc(doc(db, "sm_dealers", currentUser.uid)).then(d => {
            if (d.exists()) {
              setUserRole("dealer");
            } else {
              setUserRole("farmer");
            }
          });
        }
      }
    );
    return () => unsubCompany();
  }, [currentUser]);

  // Cross-app subscription: when ki_users subscriptionTier changes to "pro",
  // write seedmapPlan = "premium" so SeedMap AI gets access automatically.
  useEffect(() => {
    if (!currentUser || !userProfile) return;
    const hasPro     = userProfile.subscriptionTier === "pro";
    const syncedAlready = userProfile.seedmapPlan === "premium";
    if (hasPro && !syncedAlready) {
      // Write into ki_users so all SeedMap AI components pick it up via userProfile
      setDoc(
        doc(db, "ki_users", currentUser.uid),
        { seedmapPlan: "premium", seedmapPlanSyncedAt: Timestamp.now() },
        { merge: true }
      ).catch(() => {});
    } else if (!hasPro && syncedAlready) {
      // Downgrade: remove seedmap premium if fassalkart plan revoked
      setDoc(
        doc(db, "ki_users", currentUser.uid),
        { seedmapPlan: "free" },
        { merge: true }
      ).catch(() => {});
    }
  }, [currentUser, userProfile?.subscriptionTier, userProfile?.seedmapPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Load farmer profile from ki_users collection */
  const loadUserProfile = async (uid, updateLastLogin = false) => {
    try {
      const ref = doc(db, USERS_COLLECTION, uid);
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) {
        setUserProfile({ id: docSnap.id, ...docSnap.data() });
        if (updateLastLogin) {
          setDoc(ref, { lastLogin: Timestamp.now() }, { merge: true }).catch(() => {});
        }
      } else {
        setUserProfile(null);
      }
    } catch (error) {
      console.error('Error loading user profile:', error.message);
      setUserProfile(null);
    }
  };

  /** Create base profile in ki_users */
  const createBaseProfile = async (user, extra = {}) => {
    const ref = doc(db, USERS_COLLECTION, user.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        uid:      user.uid,
        name:     user.displayName || extra.name || '',
        email:    user.email || extra.email || '',
        phone:    user.phoneNumber || extra.phone || '',
        photoURL: user.photoURL || '',
        role:     'farmer',
        subscriptionTier: 'free',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        onboardingComplete: false,
        ...extra
      });
    }
    await loadUserProfile(user.uid);
  };

  // ─── Phone OTP ─────────────────────────────────────────────────────────────

  const setupRecaptcha = (containerId = 'recaptcha-container') => {
    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } catch (_) {}
    // Pass DOM element directly (not string ID) to avoid deprecation warning in Firebase 12+
    const container = typeof containerId === 'string'
      ? (document.getElementById(containerId) || containerId)
      : containerId;
    window.recaptchaVerifier = new RecaptchaVerifier(auth, container, {
      size: 'invisible',
      callback: () => {}
    });
    return window.recaptchaVerifier;
  };

  const login = async (phoneNumber) => {
    const appVerifier = setupRecaptcha('recaptcha-container');
    const result = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
    setConfirmationResult(result);
    return result;
  };

  const verifyOTP = async (otp) => {
    if (!confirmationResult) throw new Error('No OTP sent. Please request OTP first.');
    const result = await confirmationResult.confirm(otp);
    await createBaseProfile(result.user);
    return result.user;
  };

  // ─── Email / Password ───────────────────────────────────────────────────────

  const loginWithEmail = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await loadUserProfile(result.user.uid);
    return result.user;
  };

  const registerWithEmail = async (email, password, name = '') => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (name) await updateProfile(result.user, { displayName: name });
    await createBaseProfile(result.user, { name, email });
    return result.user;
  };

  const sendPasswordReset = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  // ─── Google ─────────────────────────────────────────────────────────────────

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    try {
      // Try popup first
      const result = await signInWithPopup(auth, provider);
      await createBaseProfile(result.user);
      return result.user;
    } catch (err) {
      // If popup blocked or COOP issue, fall back to redirect
      if (
        err.code === 'auth/popup-blocked' ||
        err.code === 'auth/popup-closed-by-user' ||
        err.code === 'auth/cancelled-popup-request'
      ) {
        await signInWithRedirect(auth, provider);
        return null; // page will reload after redirect
      }
      throw err;
    }
  };

  // ─── Update / Logout ────────────────────────────────────────────────────────

  const updateUserProfile = async (data) => {
    if (!currentUser) throw new Error('Not logged in');
    const ref = doc(db, USERS_COLLECTION, currentUser.uid);
    // Use setDoc+merge so it creates the document if it doesn't exist yet
    // (updateDoc throws if the document is missing — common for new Google/Phone users)
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
    await loadUserProfile(currentUser.uid);
  };

  const logout = async () => {
    await signOut(auth);
    setCurrentUser(null);
    setUserProfile(null);
    setCompanyProfile(null);
    setUserRole(null);
  };

  const refreshProfile = async () => {
    if (currentUser) await loadUserProfile(currentUser.uid);
  };

  const reloadProfile = refreshProfile;

  // ─── Role helpers ───────────────────────────────────────────────────────────
  const isAdmin   = () => userProfile?.role === 'admin';
  const isCompany = () => userProfile?.role === 'company';
  const isOfficer = () => userProfile?.role === 'officer';
  const isFarmer  = () => !userProfile || userProfile?.role === 'farmer';
  const isPro     = () => userProfile?.subscriptionTier === 'pro';

  const value = {
    currentUser,
    userProfile,
    loading,
    confirmationResult,
    // Phone OTP
    login,
    verifyOTP,
    // Email
    loginWithEmail,
    registerWithEmail,
    sendPasswordReset,
    // Google
    loginWithGoogle,
    // Profile
    updateUserProfile,
    logout,
    refreshProfile,
    reloadProfile,
    // Role helpers
    isAdmin,
    isCompany,
    isOfficer,
    isFarmer,
    isPro,
    // SeedMap AI roles
    companyProfile,
    setCompanyProfile,
    userRole,
    // Dual-portal helpers — true when the user has a doc in both portals
    hasFarmerProfile:  !!userProfile,
    hasCompanyProfile: !!companyProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
